/* eslint-disable max-lines */
import { useCameras } from '@hakimo-ui/hakimo/data-access';
import {
  Alert,
  Button,
  Checkbox,
  Checkboxes,
  CheckboxItem,
  HakimoSpinner,
  InputField,
  Modal,
} from '@hakimo-ui/shared/ui-base';
import { useEffect, useRef, useState } from 'react';
import {
  useAlarmGroupCamera,
  useAlarmGroupStatic,
} from '../alarm-provider/AlarmGroupProvider';

interface Props {
  locationName: string;
  onClose: () => void;
}

export function AddCameraFromTenantModal(props: Props) {
  const { locationName, onClose } = props;
  const [camSearchValue, setCamSearchValue] = useState('');

  const [cameraItems, setCameraItems] = useState<CheckboxItem<string>[]>([]);
  const [selectedItems, setSelectedItems] = useState<CheckboxItem<string>[]>(
    []
  );

  const { alarmCameraIds, tenantAdditionalCameraIds } = useAlarmGroupStatic();
  const { updateExtraCamFromTenant } = useAlarmGroupCamera();

  const initialAlarmCameraIdsRef = useRef<string[]>(alarmCameraIds);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const params = new URLSearchParams();
  params.append('locations', locationName);
  const { isFetching, isError, error, data } = useCameras(params.toString());

  useEffect(() => {
    const allCams = data?.items ?? [];
    const selectedCams: CheckboxItem<string>[] = [];
    const allItems: CheckboxItem<string>[] = [];
    const initialAlarmIds = initialAlarmCameraIdsRef.current;
    allCams.forEach((cam) => {
      const item = {
        id: cam.id,
        value: cam.id,
        label: cam.name,
        disabled: false,
      };
      if (initialAlarmIds.includes(cam.id)) {
        item.disabled = true;
        selectedCams.push(item);
      }
      if (tenantAdditionalCameraIds.includes(cam.id)) {
        selectedCams.push(item);
      }
      allItems.push(item);
    });
    setCameraItems(allItems);
    setSelectedItems(selectedCams);
    setIsSelectAllChecked(allCams.length === selectedCams.length);
  }, [data?.items, tenantAdditionalCameraIds]);

  const handleSubmit = () => {
    const allCams = data?.items ?? [];
    const selectedCamIds = selectedItems.map((cam) => cam.id);
    const newSelectedCameras = allCams.filter(
      (cam) =>
        !alarmCameraIds.includes(cam.id) && selectedCamIds.includes(cam.id)
    );
    updateExtraCamFromTenant(newSelectedCameras);
    onClose();
  };

  const onChange = (val: CheckboxItem<string>[]) => {
    setSelectedItems(val);
    const allCams = data?.items ?? [];
    setIsSelectAllChecked(val.length === allCams.length);
  };

  const onSearchValChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setCamSearchValue(val);
    const allCams = data?.items ?? [];
    const updatedCams = allCams
      .filter((cam) => cam.name.toLowerCase().includes(val.toLowerCase()))
      .map((cam) => {
        const item = {
          id: cam.id,
          value: cam.id,
          label: cam.name,
          disabled: false,
        };
        if (alarmCameraIds.includes(cam.id)) {
          item.disabled = true;
        }
        return item;
      });
    setCameraItems(updatedCams);
  };

  const onSelectAllChange = (isSelectAllVal: boolean) => {
    setIsSelectAllChecked(isSelectAllVal);
    const allCams = data?.items ?? [];
    const selectedCams: CheckboxItem<string>[] = [];
    allCams.forEach((cam) => {
      const item = {
        id: cam.id,
        value: cam.id,
        label: cam.name,
        disabled: false,
      };
      if (!isSelectAllVal) {
        if (alarmCameraIds.includes(cam.id)) {
          selectedCams.push(item);
        }
        return;
      }
      if (alarmCameraIds.includes(cam.id)) {
        item.disabled = true;
      }
      selectedCams.push(item);
    });
    setSelectedItems(selectedCams);
  };

  const actions = (
    <>
      <Button variant="primary" onClick={handleSubmit}>
        Submit
      </Button>
      <Button onClick={onClose}>Cancel</Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Add cameras for live view"
      footer={actions}
    >
      <div className="relative mx-8 my-4 h-[30rem] min-w-96 overflow-auto px-1 transition-all duration-300 ease-in-out">
        {isError && <Alert type="error">{error.message}</Alert>}
        <h2 className="font-semibold">Select cameras at {locationName}</h2>
        <div className="sticky top-0 my-2 flex">
          <InputField
            label=""
            onChange={onSearchValChange}
            value={camSearchValue}
            type="search"
            placeholder="Search with camera name..."
          />
        </div>
        {isFetching && (
          <div className="flex items-center justify-center">
            <HakimoSpinner />
          </div>
        )}
        <div className="border-onlight-line-1 dark:bg-dark-bg dark:border-ondark-line-2 sticky top-[2.4rem] border-b bg-white py-2 font-semibold">
          <Checkbox
            checked={isSelectAllChecked}
            label="Select All"
            onChange={onSelectAllChange}
          />
        </div>
        {cameraItems.length > 0 && (
          <div>
            <Checkboxes
              items={cameraItems}
              value={selectedItems}
              onChange={onChange}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default AddCameraFromTenantModal;
